// ============================================================
// Analitik ve yönetim raporları (§6)
//
// Panel günlük operasyonu gösteriyor; kurum sahibinin karar almasını
// sağlayacak eğilim verisi yoktu.
//
// Buradaki en önemli rapor öğretmen/çocuk oranı uyumu: `class.ratio`
// alanı baştan beri vardı ama kimse doğrulamıyordu. Kapasite dolarken
// oran ihlali sessizce oluşuyor ve denetimde ortaya çıkıyor.
// ============================================================

import { BUGUN, DONEM } from "../config.js";
import { B, subeBaglaminda, subeBulHerYerde } from "./context.js";
import { donemEkle } from "../lib/fmt.js";
import { aylikOzet, donemGunleri } from "./attendance.js";
import { faturaDurumu, faturaTutari, tahsilEdilen } from "./invoices.js";
import { HUNI, KAYNAKLAR } from "../views/basvurular.js";

const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());

const gunFark = (a, b) =>
  Math.round((new Date(a + "T00:00:00Z") - new Date(b + "T00:00:00Z")) / 86400000);

/** Son N dönemi verir (eskiden yeniye). */
export function sonDonemler(adet = 6, son = DONEM) {
  const list = [];
  for (let i = adet - 1; i >= 0; i--) list.push(donemEkle(son, -i));
  return list;
}

// ---------- Doluluk ----------

/**
 * Dönem bazlı doluluk. Bir öğrenci o dönemde kayıtlıysa sayılır —
 * kayıt tarihine göre geriye dönük hesaplanır.
 */
export function dolulukTrendi(branchId, adet = 6) {
  const t = sube(branchId);
  const kapasite = t.classes.reduce((a, c) => a + c.capacity, 0);

  return sonDonemler(adet).map(donem => {
    const sonGun = `${donem}-28`;
    const kayitli = t.students.filter(s => s.enrolledAt <= sonGun).length;
    return {
      donem,
      kayitli,
      kapasite,
      oran: kapasite ? Math.round((kayitli / kapasite) * 100) : 0,
      bosKontenjan: kapasite - kayitli,
    };
  });
}

/** Boş kontenjanın aylık maliyeti — kaçırılan gelir. */
export function bosKontenjanMaliyeti(branchId) {
  const t = sube(branchId);
  return t.classes.map(c => {
    const dolu = t.students.filter(s => s.classId === c.id).length;
    const bos = c.capacity - dolu;
    return {
      sinif: c,
      dolu,
      bos,
      kayipGelir: bos * c.tuition,
      dolulukOrani: Math.round((dolu / c.capacity) * 100),
    };
  }).sort((a, b) => b.kayipGelir - a.kayipGelir);
}

// ---------- Öğretmen / çocuk oranı ----------

/**
 * Oran uyum kontrolü.
 *
 * `class.ratio` "1 öğretmen / 4 çocuk" biçiminde. Sınıfa atanmış
 * personel sayısı ile mevcut çocuk sayısı karşılaştırılır. İhlal
 * denetimde doğrudan sorun; ayrıca çocuk güvenliği meselesi.
 *
 * İki ayrı hesap yapılır:
 *   kayitli  — sınıfa kayıtlı tüm çocuklar (planlama için)
 *   bugun    — bugün gelmiş çocuklar (operasyonel gerçek)
 */
export function oranUyumu(branchId) {
  const t = sube(branchId);

  return t.classes.map(c => {
    const gereken = oranCoz(c.ratio);
    const ogretmenler = t.staff.filter(k => k.classId === c.id && k.status === "aktif");
    const kayitli = t.students.filter(s => s.classId === c.id);
    const bugun = kayitli.filter(s => t.attendance[s.id]?.status === "geldi");

    const kapasiteIhtiyac = gereken ? Math.ceil(c.capacity / gereken) : null;
    const kayitliIhtiyac = gereken ? Math.ceil(kayitli.length / gereken) : null;
    const bugunIhtiyac = gereken ? Math.ceil(bugun.length / gereken) : null;

    return {
      sinif: c,
      gerekenOran: gereken,
      ogretmen: ogretmenler.length,
      ogretmenler,
      kayitli: kayitli.length,
      bugun: bugun.length,
      kapasiteIhtiyac,
      kayitliIhtiyac,
      bugunIhtiyac,
      // İhlal: bugünkü mevcut için yeterli öğretmen yok.
      ihlal: bugunIhtiyac != null && ogretmenler.length < bugunIhtiyac,
      // Risk: kapasite dolarsa öğretmen yetmeyecek.
      kapasiteRiski: kapasiteIhtiyac != null && ogretmenler.length < kapasiteIhtiyac,
      eksikOgretmen: Math.max(0, (bugunIhtiyac ?? 0) - ogretmenler.length),
    };
  });
}

/** "1 öğretmen / 4 çocuk" → 4 */
export function oranCoz(ratio) {
  const m = String(ratio ?? "").match(/(\d+)\s*çocuk/i);
  return m ? Number(m[1]) : null;
}

// ---------- Tahsilat ----------

/** Dönem bazlı tahsilat oranı ve ortalama gecikme. */
export function tahsilatTrendi(branchId, adet = 6) {
  const t = sube(branchId);

  return sonDonemler(adet).map(donem => {
    const faturalar = t.payments.filter(p => p.period === donem);
    let tahakkuk = 0, tahsil = 0, gecikmeGun = 0, gecikenAdet = 0;

    for (const p of faturalar) {
      const s = t.students.find(x => x.id === p.studentId);
      if (!s) continue;
      tahakkuk += faturaTutari(s, p);
      tahsil += tahsilEdilen(p);

      for (const h of p.transactions ?? []) {
        const fark = gunFark(h.date, p.due);
        if (fark > 0) { gecikmeGun += fark; gecikenAdet++; }
      }
    }

    return {
      donem,
      tahakkuk,
      tahsil,
      acik: tahakkuk - tahsil,
      oran: tahakkuk ? Math.round((tahsil / tahakkuk) * 100) : null,
      ortGecikme: gecikenAdet ? Math.round(gecikmeGun / gecikenAdet) : 0,
    };
  });
}

/** Açık bakiye yaşlandırma — muhasebenin standart raporu. */
export function bakiyeYaslandirma(branchId) {
  const t = sube(branchId);
  // Kovalar ÖRTÜŞMEMELİ. Vadesi bugün dolan fatura (yas = 0) gecikmeye
  // girer, "vadesi gelmemiş" sayılmaz — bu yüzden ilk kovanın üst
  // sınırı -1'dir.
  const kovalar = [
    { id: "vadesi_gelmemis", label: "Vadesi gelmemiş", alt: -Infinity, ust: -1, tutar: 0, adet: 0 },
    { id: "0_30", label: "0–30 gün", alt: 0, ust: 30, tutar: 0, adet: 0 },
    { id: "31_60", label: "31–60 gün", alt: 31, ust: 60, tutar: 0, adet: 0 },
    { id: "61_90", label: "61–90 gün", alt: 61, ust: 90, tutar: 0, adet: 0 },
    { id: "90_ustu", label: "90+ gün", alt: 91, ust: Infinity, tutar: 0, adet: 0 },
  ];

  for (const p of t.payments) {
    const s = t.students.find(x => x.id === p.studentId);
    if (!s || faturaDurumu(s, p) === "odendi") continue;

    const kalan = faturaTutari(s, p) - tahsilEdilen(p);
    const yas = gunFark(BUGUN, p.due);
    const kova = kovalar.find(k => yas >= k.alt && yas <= k.ust);
    if (kova) { kova.tutar += kalan; kova.adet++; }
  }

  return kovalar;
}

// ---------- Devamsızlık ----------

/** Sürekli devamsız çocuklar — ayrılma sinyali. */
export function devamsizlikDeseni(branchId, period = DONEM) {
  const t = sube(branchId);
  const gunSayisi = donemGunleri(period).length;

  return t.students
    .map(s => {
      const o = aylikOzet(t, s, period);
      return {
        student: s,
        ...o,
        gunSayisi,
        // Bildirimsiz devamsızlık, izinli olmaktan farklı bir sinyal.
        bildirimsizOran: o.islenen ? Math.round((o.gelmedi / o.islenen) * 100) : 0,
      };
    })
    .filter(x => x.islenen > 0)
    .sort((a, b) => (a.devamOrani ?? 100) - (b.devamOrani ?? 100));
}

// ---------- Başvuru hunisi ----------

/** Kaynak bazlı dönüşüm — hangi kanal gerçekten kayıt getiriyor? */
export function huniDonusumu(branchId) {
  const t = sube(branchId);

  const kaynaklar = KAYNAKLAR.map(kaynak => {
    const list = t.applications.filter(a => a.source === kaynak);
    const kayit = list.filter(a => a.stage === "kayit").length;
    const kayip = list.filter(a => a.stage === "kaybedildi").length;
    const kapanan = kayit + kayip;

    return {
      kaynak,
      toplam: list.length,
      kayit,
      kayip,
      aktif: list.length - kapanan,
      donusum: kapanan ? Math.round((kayit / kapanan) * 100) : null,
    };
  }).filter(x => x.toplam > 0)
    .sort((a, b) => b.toplam - a.toplam);

  // Aşamada ortalama bekleme süresi.
  const asamalar = HUNI.map(h => {
    const list = t.applications.filter(a => a.stage === h.id);
    const gunler = list.map(a => gunFark(BUGUN, a.stageSince));
    return {
      asama: h,
      adet: list.length,
      ortBekleme: gunler.length ? Math.round(gunler.reduce((a, b) => a + b, 0) / gunler.length) : 0,
      enUzun: gunler.length ? Math.max(...gunler) : 0,
    };
  });

  const kayipNedenleri = {};
  for (const a of t.applications.filter(x => x.stage === "kaybedildi")) {
    const n = a.lostReason || "Belirtilmedi";
    kayipNedenleri[n] = (kayipNedenleri[n] ?? 0) + 1;
  }

  return {
    kaynaklar,
    asamalar,
    kayipNedenleri: Object.entries(kayipNedenleri)
      .map(([neden, adet]) => ({ neden, adet }))
      .sort((a, b) => b.adet - a.adet),
  };
}

// ---------- Öğrenci devamlılığı ----------

/** Kaç çocuk kaç yıldır kurumda — sadakat göstergesi. */
export function devamlilik(branchId) {
  const t = sube(branchId);
  const kovalar = [
    { label: "1 yıldan az", alt: 0, ust: 1, adet: 0 },
    { label: "1–2 yıl", alt: 1, ust: 2, adet: 0 },
    { label: "2–3 yıl", alt: 2, ust: 3, adet: 0 },
    { label: "3+ yıl", alt: 3, ust: Infinity, adet: 0 },
  ];

  let toplamYil = 0;
  for (const s of t.students) {
    const yil = gunFark(BUGUN, s.enrolledAt) / 365.25;
    toplamYil += yil;
    const k = kovalar.find(x => yil >= x.alt && yil < x.ust);
    if (k) k.adet++;
  }

  return {
    kovalar,
    ortalamaYil: t.students.length ? Math.round((toplamYil / t.students.length) * 10) / 10 : 0,
    kardesOrani: kardesOrani(t),
  };
}

/** Aynı velinin birden fazla çocuğu — en güçlü memnuniyet göstergesi. */
function kardesOrani(t) {
  const veliSayaci = {};
  for (const s of t.students) veliSayaci[s.parent] = (veliSayaci[s.parent] ?? 0) + 1;
  const cokCocuklu = Object.values(veliSayaci).filter(n => n > 1).length;
  const veliSayisi = Object.keys(veliSayaci).length;
  return {
    veli: veliSayisi,
    cokCocuklu,
    oran: veliSayisi ? Math.round((cokCocuklu / veliSayisi) * 100) : 0,
  };
}

// ---------- Özet ----------

export function analitikOzeti(branchId) {
  const bid = branchId ?? B().id;
  return subeBaglaminda(bid, () => {
    const oran = oranUyumu(bid);
    const doluluk = dolulukTrendi(bid);
    const tahsilat = tahsilatTrendi(bid);
    const devamsiz = devamsizlikDeseni(bid);

    return {
      doluluk,
      sonDoluluk: doluluk.at(-1),
      dolulukDegisim: doluluk.length > 1
        ? doluluk.at(-1).oran - doluluk.at(-2).oran : 0,
      oranIhlali: oran.filter(x => x.ihlal).length,
      kapasiteRiski: oran.filter(x => x.kapasiteRiski).length,
      sonTahsilat: tahsilat.at(-1),
      dusukDevam: devamsiz.filter(x => x.devamOrani != null && x.devamOrani < 80).length,
      kayipGelir: bosKontenjanMaliyeti(bid).reduce((a, x) => a + x.kayipGelir, 0),
    };
  });
}
